import { useCallback, useContext, useEffect, useState } from "react"

//formik
import { Form, Formik } from "formik"
import * as Yup from "yup"
import FormikTextInput from "../../../../helpers/formikInputs/FormikTextInput"
import FormikCheckBox from "../../../../helpers/formikInputs/FormikCheckboxInput"
import FormikDropDown from "../../../../helpers/formikInputs/FormikDropDown"

//Kiosk components
import KioskAdminOrganizationLogo from "./KioskAdminOrganizationLogo"
import {
  kioskApiGetOrganization,
  kioskApiPostOrganization,
} from "../../kioskApi"

//PrimeReact
import { Button } from "primereact/button"

//SOAR Components
import { UserContext } from "../../../../context/userContext"
import { LoaderMedium } from "../../../../components/Loaders"
import { LogError } from "../../../../helpers/logger"

//styles
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import myStyles from "../../../../styles/kiosk/KioskAdminOrganization.module.css"
import InactiveKioskLabel from "../InactiveKioskLabel"
import formStyles from "../../../../styles/Form-Inputs.module.css"
import { classNames } from "primereact/utils"
import { useNavigate } from "react-router-dom"
import { Tooltip } from "primereact/tooltip"

export default function KioskAdminOrganization({
  kiosk_organization_id,
  organization_id_stamp,
  organization_id,
  organization_name,
  isParentLoading,
  onOrganizationChange,
}) {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [organizationProfile, setOrganizationProfile] = useState({
    id: undefined,
    organizationId: organization_id,
    name: organization_name,
    allowAnonymousActivityLogin: true,
    logoSize: 150,
    locations: [],
    expiry: "24hours",
    sandboxMode: true,
  })

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Please specify an organization name"),
  })

  const loadOrganizationProfile = useCallback(async () => {
    try {
      if (kiosk_organization_id) {
        setIsLoading(true)
        const organization = await kioskApiGetOrganization(
          userCtx.tokenKiosk,
          kiosk_organization_id
        )
        if (organization) {
          const expiry = organization.expiry ?? "24hours"
          if (organization)
            setOrganizationProfile({
              ...organization,
              name: organization_name,
              sandboxMode: organization.sandbox,
              expiry: expiry,
            })
          setIsLoading(false)
        } else {
          setOrganizationProfile(undefined)
          setIsLoading(false)
          userCtx.setNotificationError("Organization profile not found")
        }
      } else {
        setOrganizationProfile(undefined)
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load organization profile", error)
      setOrganizationProfile(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk_organization_id])

  useEffect(() => {
    loadOrganizationProfile()
  }, [loadOrganizationProfile])

  const handleSubmit = async (values) => {
    try {
      const result = await kioskApiPostOrganization(userCtx.tokenKiosk, values)
      return result
    } catch (error) {
      LogError("Unable to save Organization values", error)
      userCtx.setNotificationError("Unable to submit changes")
    }
  }

  if (isLoading || isParentLoading) {
    return <LoaderMedium />
  } else if (!kiosk_organization_id && organization_id) {
    return <InactiveKioskLabel />
  } else if (!kiosk_organization_id && !organization_id) {
    return <>No organization selected</>
  } else if (!organizationProfile) {
    return <>Organization not found</>
  }

  const handleNavigatetoOrganization = async () => {
    let path = `../form/organization/1/${organization_id_stamp}`

    //If the currently selected organization is not the organization in context, put it in context
    if (userCtx.session.user.organization_id !== organization_id) {
      onOrganizationChange(true)
      await userCtx.changeOrganization(organization_id)
      onOrganizationChange(false)
    }

    //Retrieve the organization idStamp for the organization template form

    navigate(path, {
      replace: true,
    })
  }

  return (
    <Formik
      initialValues={organizationProfile}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        if (await handleSubmit(values)) {
          resetForm({ values })
        }
      }}
    >
      {({ handleSubmit, isSubmitting, dirty }) => (
        <Form onSubmit={handleSubmit} autoComplete="off">
          <Tooltip target=".tt-organizationName" />
          <div className={myStyles.gridLayout}>
            <div className={myStyles.gridLayoutHeader}></div>
            <div className={myStyles.gridLayoutUnlock}>
              <div
                className={classNames(kioskStyles.container, formStyles.field)}
              >
                <label>Name</label>
                <div style={{ marginBottom: "10px" }}>
                  <small style={{ color: "gray" }}>
                    The organization name is displayed on the home page of the
                    Kiosk interface. <br />
                    To amend the name, please use the{" "}
                    <span
                      className={classNames(
                        myStyles.organizationNavigate,
                        "tt-organizationName"
                      )}
                      data-pr-tooltip={`Clicking on this link will ${
                        userCtx.session.user.organization_id !== organization_id
                          ? "select the current organization globally and "
                          : ""
                      } navigate to the organization management screen`}
                      data-pr-position="bottom"
                      onClick={handleNavigatetoOrganization}
                    >
                      organization management template
                    </span>
                  </small>
                </div>
                <FormikTextInput name={"name"} disabled={true} />
                <FormikTextInput
                  label={"Unlock code"}
                  helpText={
                    "This is the code used to unlock the Kiosk. It is read-only to maintain uniqueness in the system."
                  }
                  name={"code"}
                  disabled={true}
                />
              </div>
              <div
                className={`${kioskStyles.container} ${kioskStyles.containerSpaceTop}`}
              >
                <FormikDropDown
                  label={"Unlock Expiry"}
                  options={[
                    { label: "1 hour", value: "1hour" },
                    { label: "2 hours", value: "2hours" },
                    { label: "8 hours", value: "8hours" },
                    { label: "12 hours", value: "12hours" },
                    { label: "24 hours", value: "24hours" },
                    { label: "2 days", value: "2days" },
                    { label: "5 days", value: "5days" },
                    { label: "7 days", value: "7days" },
                    { label: "Never", value: "never" },
                  ]}
                  name={"expiry"}
                  optionLabel={"label"}
                  optionValue={"value"}
                  helpText={
                    "This sets whether Kiosk should automatically lock after a specified time"
                  }
                />
              </div>
            </div>
            <div className={myStyles.gridLayoutActivity}>
              <div
                className={`${kioskStyles.container} ${kioskStyles.containerSpaceTop}`}
              >
                <div className={myStyles.activityLayout}>
                  <div className={myStyles.activityLayoutLabel}>
                    <label>Anonymous Activity Login</label>
                  </div>
                  <div className={myStyles.activityLayoutHelpText}>
                    <small
                      style={{ color: "gray" }}
                    >{`Choose whether or not to allow anonymous "Activity-Check-in" login for your organization using the "0000" unique ID on the check-in page`}</small>
                  </div>
                  <div className={myStyles.activityLayoutCheck}>
                    <FormikCheckBox
                      name={"allowAnonymousActivityLogin"}
                      disabled={isSubmitting || isLoading || isParentLoading}
                      useSwitch
                    />
                  </div>
                </div>
              </div>
              <div
                className={`${kioskStyles.container} ${kioskStyles.containerSpaceTop}`}
              >
                <div className={myStyles.activityLayout}>
                  <div className={myStyles.activityLayoutLabel}>
                    <label>Sandbox Mode</label>
                  </div>
                  <div className={myStyles.activityLayoutHelpText}>
                    <small style={{ color: "gray", display: "block" }}>
                      {`Choose whether to enable or disable sandbox mode for your organization.`}
                    </small>
                    <small style={{ color: "gray", display: "block" }}>
                      {`Tip: Use this to test out new features or configuration updates. While enabled form submissions and activity check-in data will not be saved`}
                    </small>
                  </div>
                  <div className={myStyles.activityLayoutCheck}>
                    <FormikCheckBox
                      name={"sandboxMode"}
                      disabled={isSubmitting || isLoading || isParentLoading}
                      useSwitch
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={myStyles.gridLayoutLogo}>
              <KioskAdminOrganizationLogo
                organization_id={organization_id}
                disabled={isSubmitting || isLoading || isParentLoading}
              />
            </div>
            <div className={myStyles.gridLayoutAction}>
              <Button
                label="Save"
                className={kioskStyles.button}
                disabled={
                  !dirty || isSubmitting || isLoading || isParentLoading
                }
                type="submit"
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
